import { Avatar, Button, Dialog, DialogContent, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useMeCore } from '../../core/modules/me';
import { useGlobalCore } from '../../core/modules/global';
import { ME_USER_DIALOG } from '../../core/constants/dialog';
import { COUNTRY } from '../../core/constants/country';
import Spinner from './Spinner';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { PhotoCamera } from '@mui/icons-material';

interface FormData {
  name: string;
  city?: string;
  country?: string;
  phone?: string;
  rut?: string;
}

const schema = yup.object().shape({
  name: yup.string().required('You must enter a name'),
  city: yup.string(),
  country: yup.string(),
  phone: yup.string(),
  rut: yup.string(),
});

const defaultValues: FormData = {
  name: '',
  city: '',
  country: '',
  phone: '',
  rut: '',
};

function UserDialog() {
  const { t } = useTranslation();
  const { dialogs, closeDialog } = useGlobalCore();
  const { user, putMeUser, getMeUser } = useMeCore();

  const [loading, setLoading]  = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null);

  const { control, formState, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { errors } = formState;

  useEffect(() => {
    if (user) {
      setValue('name', user.name);
      setValue('city', user.city || '');
      setValue('country', user.country || '');
      setValue('phone', user.phone || '');
      setValue('rut', user.phone || '');
    }
  }, [user, setValue]);

  useEffect(() => {
    return () => {
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
    };
  }, [previewAvatarUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedAvatar(file);
      const newPreviewUrl = URL.createObjectURL(file);

      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
      setPreviewAvatarUrl(newPreviewUrl);
    }
  };

  const onSubmit = useCallback(
    async ({ name, city, country, phone, rut }: FormData) => {
      setLoading(true);
      putMeUser(
        { name, city, country, phone, rut, selectedAvatar },
        () => {
          setPreviewAvatarUrl(null);
          getMeUser(() => {
            if (previewAvatarUrl) {
              URL.revokeObjectURL(previewAvatarUrl);
            }
          });
          setLoading(false);
          closeDialog(ME_USER_DIALOG);
        },
        () => {
          setLoading(false);
        }
      );
    },
    [putMeUser, selectedAvatar, getMeUser, closeDialog, previewAvatarUrl]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={() => {
        closeDialog(ME_USER_DIALOG);
      }}
      open={dialogs.includes(ME_USER_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Perfil
              </Typography>
            </div>

            <div className="flex flex-col justify-center items-center mb-32">
              {previewAvatarUrl || user?.avatar ? (
                <Avatar
                  alt="user photo"
                  src={previewAvatarUrl || user?.avatar}
                  sx={{ width: 100, height: 100 }}
                />
              ) : (
                <Avatar sx={{ width: 100, height: 100 }}>
                  {user && user.name ? user.name[0] : user?.email[0]}
                </Avatar>
              )}
              <input
                accept="image/jpeg"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="avatar-upload">
                <Button color="primary" aria-label="upload picture" component="span" className="mt-2">
                  <PhotoCamera />
                  Elegir Avatar
                </Button>
              </label>
            </div>

            <form name="infoForm" noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className="flex items-center w-full">
                <div className="ms-10 flex-1">
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label={t('label.name')}
                        id="name"
                        error={!!errors.name}
                        helperText={errors?.name?.message}
                        variant="outlined"
                        disabled={loading}
                        required
                        fullWidth
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="phone"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label={t('label.phone')}
                        id="phone"
                        error={!!errors.phone}
                        helperText={errors?.phone?.message}
                        variant="outlined"
                        disabled={loading}
                        fullWidth
                      />
                    )}
                  />

                  <FormControl className="flex w-full mb-32" variant="outlined">
                    <InputLabel id="country-select-label">{t('label.country')}</InputLabel>
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="country-select-label"
                          id="country-select"
                          label="Country"
                        >
                          <MenuItem value="">
                            <em>{t('select.placeholder')}</em>
                          </MenuItem>
                          {COUNTRY.map((country) => (
                            <MenuItem value={country.name} key={country.code}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>

                  <Controller
                    control={control}
                    name="city"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t('label.city')}
                        id="city"
                        error={!!errors.city}
                        helperText={errors?.city?.message}
                        variant="outlined"
                        disabled={loading}
                        fullWidth
                      />
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-center mt-32">
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ borderRadius: 50 }}
                  disabled={!isEmpty(errors) || loading}
                  type="submit"
                >
                  <Spinner show={loading} />
                  {t('button.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UserDialog;
